import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  BookOpen, 
  Play, 
  Clock, 
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  HourglassIcon,
  Award,
  MapPin,
  Send,
  Eye,
  CreditCard,
  Sparkles,
  BookMarked,
  FileDown,
  ShieldCheck,
  MapPin as MapPinIcon
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ProgrammeTab,
  InfosPratiquesTab,
  ManuelTab,
  KnowledgeBaseTab,
  ParrainageTab,
} from '../components/student/StudentTabs';
import { StudentManualsTab } from '../components/student/StudentManualsTab';
import { UserDocumentsTab } from '../components/student/UserDocumentsTab';
import { DashboardShell } from '../components/dashboard/DashboardShell';

const SectionTitle = ({ kicker, title, em }) => (
  <div className="mb-6">
    <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">{kicker}</p>
    <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
      {title} {em && <span className="font-serif italic text-[#C9B896]">{em}</span>}
    </h2>
  </div>
);

const categoryImages = {
  decouverte: 'https://images.unsplash.com/photo-1625479142928-c2f2914318f2?w=400',
  complete: 'https://images.unsplash.com/photo-1676761136925-900da9b58200?w=400',
  auto: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400',
  moto: 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=400',
  nautique: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=400',
  habitat: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400',
  dessin: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400'
};

const statusColors = {
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-500', label: 'En attente' },
  accepted: { bg: 'bg-green-500/20', text: 'text-green-500', label: 'Acceptée' },
  rejected: { bg: 'bg-red-500/20', text: 'text-red-500', label: 'Refusée' },
  waitlist: { bg: 'bg-blue-500/20', text: 'text-blue-500', label: 'Liste d\'attente' }
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'accepted':
      return <CheckCircle className="w-4 h-4" />;
    case 'rejected':
      return <XCircle className="w-4 h-4" />;
    case 'waitlist':
      return <HourglassIcon className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

export const MyCoursesPage = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  
  const [enrollments, setEnrollments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const defaultTab = location.state?.tab || 'courses';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [enrollmentsData, applicationsData] = await Promise.all([
        api.getEnrollments(),
        api.getMyApplications()
      ]);
      setEnrollments(enrollmentsData);
      setApplications(applicationsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProgress = (enrollment) => {
    if (!enrollment.course?.episodes?.length) return 0;
    const completed = Object.values(enrollment.progress || {}).filter(Boolean).length;
    return (completed / enrollment.course.episodes.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-beige border-t-transparent rounded-full" />
      </div>
    );
  }

  // ────────────────────────────── Section: Mes formations
  const CoursesSection = (
    <div>
      <SectionTitle kicker="MES FORMATIONS" title="Tes" em="apprentissages." />
      {enrollments.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-lg">
          <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Aucune formation en cours</h3>
          <p className="text-muted-foreground mb-6">Vous n'êtes inscrit à aucune formation pour le moment.</p>
          <Link to={L('/formations-en-ligne')}>
            <Button className="bg-[#C9B896] text-black hover:bg-[#E0D4BA]">Découvrir nos formations</Button>
          </Link>
        </div>
      ) : (
        <div className="grid gap-6">
          {enrollments.map((enrollment) => {
            const progress = getProgress(enrollment);
            const course = enrollment.course;
            if (!course) return null;
            return (
              <div key={enrollment.id} className="bg-white border border-[#1F2A4A]/10 rounded-lg overflow-hidden hover:border-beige/50 transition-colors" data-testid={`enrollment-${enrollment.id}`}>
                <div className="grid md:grid-cols-4">
                  <div className="aspect-video md:aspect-auto relative overflow-hidden">
                    <img src={course.thumbnail_url || categoryImages[course.category] || categoryImages.complete} alt={course.title} className="w-full h-full object-cover" />
                    {progress === 100 && (
                      <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                        <Award className="w-12 h-12 text-green-500" />
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-3 p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                      <div>
                        <Badge className="bg-[#C9B896]/20 text-[#C9B896] border-beige/30 mb-2">Formation en ligne</Badge>
                        <h3 className="font-heading text-xl font-semibold">{course.title}</h3>
                      </div>
                      <Link to={L(`/apprendre/${course.id}`)}>
                        <Button className="bg-[#C9B896] text-black hover:bg-[#E0D4BA]">
                          <Play className="w-4 h-4 mr-2" />
                          {progress > 0 ? 'Continuer' : 'Commencer'}
                        </Button>
                      </Link>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progression</span>
                        <span className="font-mono text-[#C9B896]">{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><Play className="w-4 h-4" />{course.episodes?.length || 0} épisodes</span>
                        <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" />{Object.values(enrollment.progress || {}).filter(Boolean).length} terminés</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Build applications section content from the existing rendering below
  const sections = [
    { key: 'courses', label: 'Mes formations', Icon: BookOpen, badge: enrollments.length, content: CoursesSection },
    { key: 'applications', label: 'Mes candidatures', Icon: FileText, badge: applications.length, content: <ApplicationsSection applications={applications} /> },
    { key: 'programme', label: 'Programme', Icon: Calendar, content: <div><SectionTitle kicker="PROGRAMME" title="Le détail de" em="ta formation." /><ProgrammeTab enrollments={enrollments} /></div> },
    { key: 'infos', label: 'Infos pratiques', Icon: MapPinIcon, content: <div><SectionTitle kicker="INFOS PRATIQUES" title="Tout pour" em="venir à l'atelier." /><InfosPratiquesTab /></div> },
    { key: 'manuel', label: 'Manuel', Icon: FileDown, content: <div><SectionTitle kicker="MANUEL" title="Le support" em="de ta formation." /><ManuelTab enrollments={enrollments} /></div> },
    { key: 'cours', label: 'Cours & QCM', Icon: BookOpen, content: <StudentManualsTab /> },
    { key: 'kb', label: 'Base de connaissance', Icon: BookMarked, content: <div><SectionTitle kicker="BASE DE CONNAISSANCE" title="Approfondis" em="le métier." /><KnowledgeBaseTab /></div> },
    { key: 'parrainage', label: 'Parrainage', Icon: Sparkles, content: <ParrainageTab /> },
  ];

  return (
    <DashboardShell
      kicker="ESPACE ÉLÈVE"
      roleLabel="Élève"
      sections={sections}
      defaultSection={defaultTab}
      testIdRoot="student-dashboard"
      nested
    />
  );
};

// Extracted: applications section — keeps the original timeline rendering below.
// The administrative documents block is integrated here at the top so the
// candidate sees that no application can be submitted while their dossier is
// incomplete.
const ApplicationsSection = ({ applications }) => {
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  return (
  <div>
    {/* Documents administratifs — gating block */}
    <div className="mb-10" data-testid="applications-documents-block">
      <div className="mb-4">
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">DOSSIER ADMINISTRATIF</p>
        <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] tracking-tight">
          Pièces <span className="font-serif italic text-[#C9B896]">obligatoires.</span>
        </h2>
        <p className="text-xs text-[#1F2A4A]/70 mt-2 max-w-2xl">
          Dépose une fois les pièces administratives — elles seront attachées
          automatiquement à toutes tes prochaines candidatures.
        </p>
      </div>
      <UserDocumentsTab embedded />
    </div>

    {/* Suivi des candidatures */}
    <div className="pt-8 border-t border-[#1F2A4A]/10">
      <SectionTitle kicker="MES CANDIDATURES" title="Suivi" em="de tes dossiers." />
    {applications.length === 0 ? (
      <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-lg">
        <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">Aucune candidature</h3>
        <p className="text-muted-foreground mb-6">Vous n'avez pas encore déposé de candidature.</p>
        <Link to={L('/formations-sur-place')}>
          <Button className="bg-[#C9B896] text-black hover:bg-[#E0D4BA]">Voir les formations sur place</Button>
        </Link>
      </div>
    ) : (
      <div className="grid gap-5">
        {applications.map((app) => {
          const status = statusColors[app.status] || statusColors.pending;
          const courseTitle = app.course?.title || 'Formation';
          const sessionDate = app.session?.start_date
            ? format(parseISO(app.session.start_date), 'dd MMMM yyyy', { locale: fr })
            : null;

          const timelineSteps = [
            { key: 'submitted', label: 'Dossier déposé', icon: Send, completed: true, date: app.created_at ? format(parseISO(app.created_at), 'dd MMM yyyy', { locale: fr }) : null },
            { key: 'review', label: 'En cours d\'examen', icon: Eye, completed: app.status !== 'pending', active: app.status === 'pending' },
            {
              key: 'decision',
              label: app.status === 'rejected' ? 'Candidature refusée' : app.status === 'waitlist' ? 'Liste d\'attente' : 'Candidature acceptée',
              icon: app.status === 'rejected' ? XCircle : CheckCircle,
              completed: ['accepted', 'rejected', 'waitlist'].includes(app.status),
              active: false,
              negative: app.status === 'rejected',
              date: app.reviewed_at ? format(parseISO(app.reviewed_at), 'dd MMM yyyy', { locale: fr }) : null,
            },
            { key: 'enrolled', label: 'Inscription finalisée', icon: CreditCard, completed: false, active: app.status === 'accepted' },
          ];

          return (
            <div key={app.id} className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-6 hover:border-beige/40 transition-colors" data-testid={`application-${app.id}`}>
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                <div className="flex-1 min-w-0">
                  <Badge className={`${status.bg} ${status.text} border-none mb-3`}>
                    <StatusIcon status={app.status} />
                    <span className="ml-1.5">{status.label}</span>
                  </Badge>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">{courseTitle}</h3>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
                    {sessionDate && (
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-[#C9B896]" /> Session du {sessionDate}
                      </span>
                    )}
                    {app.session?.location && (
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-4 h-4 text-[#C9B896]" /> {app.session.location}
                      </span>
                    )}
                    {app.funding_source && (
                      <span className="flex items-center gap-1.5">
                        <CreditCard className="w-4 h-4 text-[#C9B896]" /> {app.funding_source}
                      </span>
                    )}
                  </div>
                </div>
                {app.status === 'accepted' && (
                  <Button className="bg-[#C9B896] text-black hover:bg-[#E0D4BA] whitespace-nowrap" data-testid={`finalize-application-${app.id}`}>
                    Finaliser l'inscription
                  </Button>
                )}
              </div>

              <div className="pt-5 border-t border-[#1F2A4A]/10">
                <p className="text-xs font-semibold text-[#C9B896] uppercase tracking-wider mb-4">Suivi de validation</p>
                <div className="relative">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {timelineSteps.map((step, idx) => {
                      const Icon = step.icon;
                      const isLast = idx === timelineSteps.length - 1;
                      return (
                        <div key={step.key} className="relative flex flex-col items-start">
                          {!isLast && (
                            <div className={`hidden md:block absolute top-4 left-10 right-0 h-0.5 ${step.completed ? 'bg-[#C9B896]' : 'bg-navy-light/50'}`} />
                          )}
                          <div className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center mb-2 ${
                            step.negative ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                              : step.completed ? 'bg-[#C9B896] text-navy'
                              : step.active ? 'bg-[#C9B896]/15 border-2 border-beige text-[#C9B896] animate-pulse'
                              : 'bg-navy-light/40 text-foreground/40 border border-[#1F2A4A]/10/40'
                          }`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <p className={`text-xs font-medium leading-tight ${step.completed || step.active ? 'text-foreground' : 'text-foreground/50'}`}>
                            {step.label}
                          </p>
                          {step.date && <p className="text-[10px] text-muted-foreground mt-1">{step.date}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
    </div>
  </div>
  );
};